import { useEffect, useState } from "react";
import { Lock, Check, ShoppingBag, Star, Zap, Trophy, Crown, Award, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TIERS = [
  {
    level: 1, name: "Starter", color: "bg-emerald-100 text-emerald-700", iconBg: "bg-emerald-500",
    icon: Star, threshold: 1,
    items: [{ key: "badge", label: "Badge numérique + accès au groupe", price: 0 }],
    desc: "Bienvenue dans le réseau",
  },
  {
    level: 2, name: "Actif", color: "bg-blue-100 text-blue-700", iconBg: "bg-blue-500",
    icon: Zap, threshold: 5,
    items: [{ key: "tshirt", label: "T-shirt Go Helper", price: 20 }],
    desc: "Tu fais partie du réseau",
  },
  {
    level: 3, name: "Fiable", color: "bg-amber-100 text-amber-700", iconBg: "bg-amber-500",
    icon: Award, threshold: 15,
    items: [{ key: "hoodie", label: "Hoodie Go Helper", price: 30 }],
    desc: "Crédibilité + appartenance",
  },
  {
    level: 4, name: "Pro", color: "bg-orange-100 text-orange-700", iconBg: "bg-orange-500",
    icon: Trophy, threshold: 30,
    items: [
      { key: "cap", label: "Casquette Go Helper", price: 15 },
      { key: "jacket_light", label: "Manteau léger Go Helper", price: 50 },
    ],
    desc: "Reconnaissance terrain",
  },
  {
    level: 5, name: "Elite", color: "bg-purple-100 text-purple-700", iconBg: "bg-gradient-to-br from-purple-500 to-pink-500",
    icon: Crown, threshold: 50,
    items: [{ key: "kit_complete", label: "Kit ambassadeur (manteau + tuque + accessoires)", price: 0 }],
    desc: "Ambassadeur officiel — gratuit pour toi",
  },
];

export default function Gear() {
  const { user } = useAuth();
  const [orderItem, setOrderItem] = useState(null);
  const [size, setSize] = useState("M");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);

  const profile = user?.helper_profile || {};
  const completed = profile.tasks_completed || 0;

  if (user.role !== "helper") {
    return (
      <div className="card-surface p-10 text-center">
        <Lock size={36} className="mx-auto text-[#94A3B8]" />
        <p className="mt-3 text-[#475569]">L'équipement Go Helper est réservé aux helpers.</p>
      </div>
    );
  }

  const currentTier = [...TIERS].reverse().find((t) => completed >= t.threshold) || TIERS[0];
  const nextTier = TIERS.find((t) => t.threshold > completed);

  const order = async () => {
    if (!orderItem) return;
    setSending(true);
    try {
      // Send via internal message to admin (no separate endpoint needed)
      const message = `Commande équipement\n\n— Article : ${orderItem.label}\n— Prix membre : ${orderItem.price === 0 ? "OFFERT" : orderItem.price + " $"}\n— Taille : ${size}\n— Notes : ${notes || "—"}\n\nMerci !`;
      await api.post("/notifications/order-gear", {
        item_key: orderItem.key,
        item_label: orderItem.label,
        price: orderItem.price,
        size,
        notes,
      });
      toast.success("Commande envoyée à l'admin Go Helper !");
      setOrderItem(null);
      setSize("M");
      setNotes("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl md:text-4xl font-heading font-black text-navy flex items-center gap-2">
          <ShoppingBag size={28} className="text-lime" /> Mon équipement Go Helper
        </h1>
        <p className="text-[#475569] mt-1">Plus tu fais de jobs, plus tu débloques ton équipement officiel à prix membre.</p>
      </div>

      {/* Progress */}
      <div className="bg-navy text-white rounded-3xl p-6 md:p-8 relative overflow-hidden noise" data-testid="gear-progress">
        <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-lime/20 blur-3xl" />
        <div className="relative">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-lime">Niveau actuel</div>
              <div className="font-heading font-black text-4xl md:text-5xl mt-1">{currentTier.name}</div>
              <div className="text-white/70 mt-1">{completed} tâche{completed > 1 ? "s" : ""} complétée{completed > 1 ? "s" : ""}</div>
            </div>
            {nextTier && (
              <div className="text-right">
                <div className="text-xs font-black uppercase tracking-wider text-white/50">Prochain niveau</div>
                <div className="font-heading font-black text-xl text-lime">{nextTier.name}</div>
                <div className="text-white/70 text-sm">Plus que {nextTier.threshold - completed} tâche{nextTier.threshold - completed > 1 ? "s" : ""}</div>
              </div>
            )}
          </div>
          {nextTier && (
            <div className="mt-5 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-lime transition-all" style={{ width: `${Math.min(100, (completed / nextTier.threshold) * 100)}%` }} />
            </div>
          )}
        </div>
      </div>

      {/* Tiers */}
      <div className="space-y-4">
        {TIERS.map((t) => {
          const Icon = t.icon;
          const unlocked = completed >= t.threshold;
          return (
            <article
              key={t.level}
              className={`card-surface p-5 md:p-6 ${unlocked ? "ring-2 ring-lime" : "opacity-90"}`}
              data-testid={`tier-${t.level}`}
            >
              <div className="flex items-start gap-4 flex-wrap">
                <div className={`h-14 w-14 rounded-2xl ${t.iconBg} text-white flex items-center justify-center shrink-0 ${unlocked ? "" : "grayscale opacity-60"}`}>
                  <Icon size={26} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`chip text-[10px] ${t.color}`}>Niveau {t.level}</span>
                    <h2 className="font-heading font-black text-navy text-xl">{t.name}</h2>
                    {unlocked ? (
                      <span className="chip-lime flex items-center gap-1"><Check size={12} /> Débloqué</span>
                    ) : (
                      <span className="chip flex items-center gap-1 bg-surface text-[#94A3B8]"><Lock size={12} /> Verrouillé</span>
                    )}
                  </div>
                  <div className="text-xs text-[#475569] mt-1">
                    {t.threshold === 1 ? "Dès ta 1re tâche" : `Après ${t.threshold} tâches complétées`} · {t.desc}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {t.items.map((it) => (
                  <div key={it.key} className={`p-4 rounded-xl border-2 ${unlocked ? "border-[#E2E8F0]" : "border-dashed border-[#CBD5E1] bg-surface/50"}`} data-testid={`item-${it.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-navy text-sm leading-tight">{it.label}</div>
                      <div className="font-heading font-black text-navy text-lg shrink-0">
                        {it.price === 0 ? "OFFERT" : `${it.price} $`}
                      </div>
                    </div>
                    {unlocked ? (
                      <button
                        onClick={() => { setOrderItem(it); setSize("M"); setNotes(""); }}
                        className="mt-3 w-full bg-lime hover:scale-[1.02] text-navy font-bold rounded-full py-2 text-sm inline-flex items-center justify-center gap-2 transition-transform"
                        data-testid={`claim-${it.key}`}
                      >
                        <ShoppingBag size={14} /> {it.price === 0 ? "Réclamer" : "Commander à prix membre"}
                      </button>
                    ) : (
                      <button disabled className="mt-3 w-full bg-surface text-[#94A3B8] font-bold rounded-full py-2 text-sm inline-flex items-center justify-center gap-2 cursor-not-allowed">
                        <Lock size={14} /> Encore {t.threshold - completed} tâche{t.threshold - completed > 1 ? "s" : ""}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </article>
          );
        })}
      </div>

      {/* Marketing message */}
      <div className="bg-gradient-to-br from-lime/15 to-lime/5 border-2 border-lime rounded-3xl p-6 md:p-8 text-center" data-testid="gear-tagline">
        <p className="font-heading font-black text-navy text-xl md:text-2xl">
          « Plus tu fais de jobs, plus tu débloques ton <span className="text-lime bg-navy px-2 rounded">équipement officiel</span>. »
        </p>
        <p className="text-[#475569] mt-2 text-sm">Chaque pièce est méritée — c'est ta marque de fierté.</p>
      </div>

      {/* Order modal */}
      {orderItem && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end md:items-center justify-center p-4" onClick={() => setOrderItem(null)} data-testid="gear-order-dialog">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-heading font-black text-xl text-navy">Commander {orderItem.label}</h3>
                <p className="text-[#475569] text-sm mt-1">
                  Prix membre : <strong className="text-navy">{orderItem.price === 0 ? "OFFERT" : orderItem.price + " $"}</strong>
                </p>
              </div>
              <button onClick={() => setOrderItem(null)} className="h-8 w-8 rounded-full bg-surface hover:bg-red-100 text-navy hover:text-red-700 flex items-center justify-center" aria-label="Fermer">
                <X size={16} />
              </button>
            </div>

            {orderItem.key !== "kit_complete" && orderItem.key !== "badge" && orderItem.key !== "cap" && (
              <div className="mt-4">
                <label className="text-sm font-bold text-navy">Taille</label>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {["XS", "S", "M", "L", "XL"].map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      className={`py-2 rounded-lg border-2 font-bold text-sm transition-colors ${size === s ? "border-navy bg-navy text-white" : "border-[#E2E8F0] text-navy hover:bg-surface"}`}
                      data-testid={`size-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4">
              <label className="text-sm font-bold text-navy">Notes (adresse de livraison, préférences…)</label>
              <textarea
                rows={3}
                className="input-base mt-1"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex: 4123 rue Saint-Denis, Montréal…"
                data-testid="order-notes"
              />
            </div>

            <div className="mt-4 bg-surface rounded-lg p-3 text-xs text-[#475569] flex items-start gap-2">
              <Mail size={14} className="text-navy shrink-0 mt-0.5" />
              <p>L'admin Go Helper recevra ta commande et te contactera pour confirmer le mode de paiement et la livraison.</p>
            </div>

            <button
              onClick={order}
              disabled={sending}
              className="btn-primary w-full mt-5 disabled:opacity-50"
              data-testid="confirm-order"
            >
              {sending ? "Envoi…" : `Envoyer la commande${orderItem.price > 0 ? ` (${orderItem.price} $)` : ""}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
